import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';
import * as Sentry from '@sentry/react';

/**
 * Mantiene la pantalla encendida MIENTRAS se drena la cola de subidas.
 *
 * ─── El problema ───────────────────────────────────────────────────────────
 *
 * Android congela el JavaScript del WebView al apagar la pantalla o pasar la
 * app a segundo plano. Es la misma razón por la que el GPS necesitó
 * `RoisBackgroundLocation` con servicio en primer plano y SQLite nativo:
 * IndexedDB no servía porque vive dentro del WebView. Una subida en curso se
 * corta ahí, sin más.
 *
 * ─── Por qué esto es una mitigación y no la solución ───────────────────────
 *
 * Si el operario cambia de aplicación, la subida se corta igual. Lo que hace
 * que eso sea aceptable NO es este envoltorio: es que la cola es duradera y va
 * foto a foto, así que al volver se reanuda y como mucho se repite UNA foto.
 * La solución completa sería subir desde la capa nativa, pero las fotos viven
 * en OPFS —dentro del sandbox del WebView, sin ruta nativa— así que exigiría
 * migrar los blobs a `@capacitor/filesystem` primero. Está descrito como
 * Fase 2 en el spec, con disparador medible.
 *
 * ─── Sin tope, pero con aviso ──────────────────────────────────────────────
 *
 * No se apaga la pantalla a la fuerza (decisión del usuario, 2026-10-09): con
 * 3G malo una subida puede tardar mucho, y si la pantalla se apaga se queda
 * parada hasta que alguien la encienda. Lo que sí se evita es colgarse: los
 * pasos de la subida tienen plazo (`mutationDeadline`, `uploadWithProgress`).
 * Si aun así la pantalla lleva encendida más de `SCREEN_AWAKE_REPORT_MS`, se
 * avisa a Sentry una vez, para saber dónde y cuándo pasa (el 2026-10-08 una
 * tablet estuvo 6 h así y no se pudo saber qué bloqueo era).
 *
 * En web es un no-op absoluto.
 */
// 5 h y no 1 h (decisión del usuario, 2026-10-09): con 3G malo un lote grande
// puede tardar horas subiendo bien, y un aviso por cada subida lenta pero sana
// sería ruido.
export const SCREEN_AWAKE_REPORT_MS = 5 * 60 * 60 * 1000;

export async function withScreenAwake<T>(work: () => Promise<T>): Promise<T> {
  if (!Capacitor.isNativePlatform()) return work();

  try {
    await KeepAwake.keepAwake();
  } catch {
    // Que no se pueda mantener la pantalla encendida no impide subir: se
    // continúa y, como mucho, el lote se reanudará al volver.
  }

  const startedAt = Date.now();
  const report = setTimeout(() => {
    Sentry.captureMessage(
      '[keep-awake] la cola de fotos lleva más de 5 h con la pantalla encendida',
      {
        level: 'warning',
        extra: { startedAt: new Date(startedAt).toISOString() },
      },
    );
  }, SCREEN_AWAKE_REPORT_MS);

  try {
    return await work();
  } finally {
    clearTimeout(report);
    try {
      await KeepAwake.allowSleep();
    } catch {
      // Ídem: liberar es best-effort. Nunca debe tapar el resultado del
      // trabajo ni convertir un éxito en un error.
    }
  }
}
